import { useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, Tv } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { entrarEmTelaCheia } from "@/lib/telaCheia";
import { indiceDaTela, TELAS } from "./telas";

/** Teclas de navegação não podem roubar a digitação nem as setas que as abas já tratam. */
function ignorarTecla(e: KeyboardEvent): boolean {
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return true;
  const alvo = e.target;
  return alvo instanceof Element && !!alvo.closest("input, textarea, select, [contenteditable=true], [role=tablist], [role=dialog], .leaflet-container");
}

/**
 * Home ("/" e "/Overview/Consulta"): as mesmas telas do modo TV, em abas. A aba fica
 * na URL (?tela=), e "Exibir na TV" abre o rodízio nela.
 */
export default function Overview() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const indice = indiceDaTela(params.get("tela"));
  const atual = TELAS[indice]!;

  const irPara = useCallback(
    (i: number) => {
      const tela = TELAS[((i % TELAS.length) + TELAS.length) % TELAS.length]!;
      setParams(
        (p) => {
          p.set("tela", tela.id);
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (ignorarTecla(e)) return;
      if (e.key === "ArrowRight") irPara(indice + 1);
      else if (e.key === "ArrowLeft") irPara(indice - 1);
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [indice, irPara]);

  function exibirNaTv() {
    // Tela cheia precisa partir do clique (gesto do usuário); a troca de rota é
    // SPA, então o documento continua em tela cheia na página da TV.
    void entrarEmTelaCheia();
    navigate(`/TV?tela=${atual.id}`);
  }

  return (
    <Tabs value={atual.id} onValueChange={(id) => irPara(indiceDaTela(id))} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => irPara(indice - 1)} aria-label="Tela anterior">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <TabsList>
            {TELAS.map((t) => (
              <TabsTrigger key={t.id} value={t.id}>
                {t.titulo}
              </TabsTrigger>
            ))}
          </TabsList>
          <Button variant="outline" size="icon" onClick={() => irPara(indice + 1)} aria-label="Próxima tela">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="outline" onClick={exibirNaTv}>
          <Tv className="mr-2 h-4 w-4" aria-hidden /> Exibir na TV
        </Button>
      </div>

      {TELAS.map(({ id, Componente }) => (
        <TabsContent key={id} value={id} className="mt-0">
          <Componente variante="padrao" />
        </TabsContent>
      ))}
    </Tabs>
  );
}
